/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Datepicker from 'react-tailwindcss-datepicker';
import { Button } from '@mui/material';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { InputDate } from '@grapecity/wijmo.input';
import { IconButton } from 'components/buttons/IconSVG';
import {
  SearchBox,
  SearchRows,
  SearchCols,
  SearchBoxRow,
  InputBox,
} from 'components/layouts/SearchBox';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { DatePickerWrap } from 'components/inputs/DatePicker';
import { NewSensorSolutionReq } from 'models/sensor/NewSensorSolutionReq';
import {
  findSensorSolReqProgressStatusList,
  getNewSensorSolReqStatusListDownload,
} from 'apis/sensor/NewSensorSolutionReqStatus';
import { ElmEmpPopup } from 'pages/common/ElmEmpPopup';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { getExcelFileName } from 'utils/ExcelUtil';
import { ExcelDownloadRequest } from 'models/common/Excel';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import { ComboBox } from 'components/selects/ComboBox';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomGrid from 'components/grids/CustomGrid';
import { CommonUtil } from 'utils/CommonUtil';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import GridSettingButtons from 'components/grids/GridSettingButtons';

interface DisplayCondition {
  dataInsUserIdNm?: string; // 등록자명
}

const NewSensorSolutionReqStatusPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);

  const [newSensorSolutionReqStatusData, setNewSensorSolutionReqStatusData] =
    useState<NewSensorSolutionReq>({});
  const [isOpenElmEmpPopup, setOpenElmEmpPopup] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>('');
  const [rowData, setRowData] = useState<NewSensorSolutionReq[]>([]);
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});
  const [snsrStatCd, setSnsrStatCd] = useState<Code[]>([]);

  useEffect(() => {
    getCommonCode();
    searchBtn();
  }, []);

  useEffect(() => {
    if (userData != '' && userData != null) {
      const userInfo = userData.userInfo[0] || {};
      setNewSensorSolutionReqStatusData({
        ...newSensorSolutionReqStatusData,
        dataInsUserId: userInfo.userId,
        dataInsUserNm: userInfo.empNm,
      });

      setDisplayCondition({
        ...displayCondition,
        dataInsUserIdNm: userInfo.empNm,
      });
    }
  }, [userData]);

  const getCommonCode = async () => {
    const snsrStatCd: Code[] = await getCommonCodeNames('SNSR_SOL_STAT_CD');
    setSnsrStatCd(snsrStatCd);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    if (e.target.name == 'dataInsUserNm') {
      setNewSensorSolutionReqStatusData({
        ...newSensorSolutionReqStatusData,
        [e.target.name]: e.target.value,
        dataInsUserId: e.target.value,
      });
    } else {
      setNewSensorSolutionReqStatusData({
        ...newSensorSolutionReqStatusData,
        [e.target.name]: e.target.value,
      });
    }
  };

  const searchBtn = async () => {
    const response = await findSensorSolReqProgressStatusList({
      ...(newSensorSolutionReqStatusData || {}),
    });
    if (response) {
      const rowData = response.data?.list || [];
      setRowData(rowData);
    }
  };

  const handleOpenNewSensorRequest = () => {
    setNewSensorSolutionReqStatusData({});
    navigate('/sensor/newSensorSolutionReqPage', {
      state: { slunId: '', isCopy: false },
    });
  };

  const handleOpenCopyNewSensorRequest = (fieldId) => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.복사할 행을 선택해 주세요.', '복사할 행을 선택해 주세요.'),
      });
      return;
    }

    const rows = selectedRows.map((o) => o.dataItem);
    navigate('/sensor/newSensorSolutionReqPage', {
      state: { slunId: rows[0].slunId, isCopy: true },
    });
  };

  const handleResetCondition = () => {
    setNewSensorSolutionReqStatusData(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: cur === 'useYn' ? 'Y' : '' }),
          {}
        ) as NewSensorSolutionReq
    );

    setDisplayCondition(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: '' }),
          {}
        ) as DisplayCondition
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchBtn();
    }
  };

  const handleDownloadExcel = () => {
    const excelData: ExcelDownloadRequest<NewSensorSolutionReq> = {
      fileName: getExcelFileName(t('snsr.label.신규센서요청현황조회', '신규센서요청현황조회')), // + '.xlsx',
      sheetName: t('snsr.label.신규센서조회요청현황', '신규센서조회요청현황'),
      header: [
        String(t('snsr.label.솔루션 요청번호', '솔루션 요청번호')),
        String(t('snsr.label.과제명', '과제명')),
        String(t('snsr.label.공정위치', '공정위치')),
        String(t('snsr.label.문제 및 현상', '문제 및 현상')),
        String(t('snsr.label.요청목적', '요청목적')),
        String(t('snsr.label.납기', '납기')),
        String(t('com.label.진행상태', '진행상태')), // 진행상태 이름으로 변경해야함
        String(t('snsr.grid.처리내역', '처리내역')),
        String(t('com.label.요청자', '요청자')), // 요청자명으로 변경필요
        String(t('snsr.grid.측정 대상 및 필요 스펙', '측정 대상 및 필요 스펙')),
        String(t('snsr.grid.현재기준', '현재기준')),
        String(t('snsr.grid.목표기준', '목표기준')),
        String(t('snsr.label.기대효과', '기대효과')),
        String(t('snsr.label.개선방향', '개선방향')),
        // String(t('com.label.첨부', '첨부')),
        String(t('snsr.grid.비고', '비고')),
        String(t('com.label.등록자', '등록자')),
        String(t('com.label.등록일', '등록일자')),
        String(t('com.label.수정자', '수정자')),
        String(t('com.label.수정일', '수정일자')),
      ],
      searchCondition: { ...newSensorSolutionReqStatusData },
    };
    getNewSensorSolReqStatusListDownload(excelData);
  };
  const [flexRef, setFlexRef] = useState<any>();
  const onInitialized = (grid) => {
    gridRef.current = grid;
    setFlexRef(grid);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);
          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });
      grid.invalidate();
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'slunId',
        header: String(t('snsr.label.솔루션 요청번호', '솔루션 요청번호')),
        align: 'center',
        width: 160,
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            navigate('/sensor/newSensorSolutionReqPage', {
              state: { slunId: ctx.item.slunId, isCopy: false },
            });
          },
        }),
      },
      {
        binding: 'misnNm',
        header: String(t('snsr.label.과제명', '과제명')),
        align: 'left',
        width: 230,
      },
      {
        binding: 'procLocCtn',
        header: String(t('snsr.label.공정위치', '공정위치')),
        align: 'left',
        width: 260,
      },
      {
        binding: 'prbPnmnCtn',
        header: String(t('snsr.label.문제 및 현상', '문제 및 현상')),
        align: 'left',
        width: 300,
      },
      {
        binding: 'reqPurpCtn',
        header: String(t('snsr.label.요청목적', '요청목적')),
        align: 'left',
        width: 300,
      },
      {
        binding: 'dueDtm',
        header: String(t('snsr.label.납기', '납기')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'slunStatus', //'slunStatCd',
        header: String(t('com.label.진행상태', '진행상태')),
        align: 'center',
        width: 108,
      },
      {
        binding: 'psgCtn',
        header: String(t('snsr.grid.처리내역', '처리내역')),
        align: 'left',
        width: 120,
      },
      {
        binding: 'rqrUserNm',
        header: String(t('com.label.요청자', '요청자')),
        align: 'center',
        width: 106,
      },
      {
        binding: 'snsrSpecCtn',
        header: String(t('snsr.grid.측정 대상 및 필요 스펙', '측정 대상 및 필요 스펙')),
        align: 'left',
        width: 300,
      },
      {
        binding: 'curCrtnCtn',
        header: String(t('snsr.grid.현재기준', '현재기준')),
        align: 'left',
        width: 280,
      },
      {
        binding: 'golCrtnCtn',
        header: String(t('snsr.grid.목표기준', '목표기준')),
        align: 'left',
        width: 280,
      },
      {
        binding: 'epcEftCtn',
        header: String(t('snsr.label.기대효과', '기대효과')),
        align: 'left',
        width: 280,
      },
      {
        binding: 'iprvMesrCtn',
        header: String(t('snsr.label.개선방향', '개선방향')),
        align: 'left',
        width: 250,
      },
      {
        binding: 'atchFileCount', // atchFileCount
        header: String(t('com.label.첨부', '첨부')),
        align: 'center',
        width: 85,
        cellTemplate: CellMaker.makeLink({
          text: '${item.atchFileCount}',
          click: (e, ctx) => {
            setFileUploadModalCondition({
              atchFileGrId: ctx.item.atchFileGrId, //node.data.atchFileGrId,
              atchFileTpCd: 'NORMAL',
              optValCtn1: 'TB_ELM_SNSR_M', // 관련 테이블 명 (확인용)
              bizName: 'snsr', // 파일저장경로 중 업무 폴더명으로 사용 (없는 경우 etc)
              target: ctx,
            });
            setOpenFileUploadModal(true);
          },
        }),
      },
      {
        binding: 'rmk',
        header: String(t('snsr.grid.비고', '비고')),
        align: 'left',
        width: 140,
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일', '등록일')),
        align: 'center',
        width: 160,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일', '수정일')),
        align: 'center',
        width: 150,
        editor: new InputDate(document.createElement('div')),
      },
    ];
  }, [isChangeColumn, flexRef]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('snsr.label.과제명', '과제명')}</label>
                <CustomInputWithSearch
                  name="misnNm"
                  placeholder={String(
                    t('snsr.msg.과제명을 입력해 주세요.', '과제명을 입력해 주세요.')
                  )}
                  value={newSensorSolutionReqStatusData?.misnNm}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.진행상태', '진행상태')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={snsrStatCd}
                  defaultValue={newSensorSolutionReqStatusData?.slunStatCd}
                  onChange={(value) =>
                    setNewSensorSolutionReqStatusData({
                      ...newSensorSolutionReqStatusData,
                      slunStatCd: value,
                    })
                  }
                />
              </SearchCols>
              <SearchCols>
                <label>{t('snsr.label.공정위치', '공정위치')}</label>
                <CustomInputWithSearch
                  name="procLocCtn"
                  placeholder={String(
                    t('snsr.msg.공정위치를 입력해 주세요.', '공정위치를 입력해 주세요.')
                  )}
                  value={newSensorSolutionReqStatusData?.procLocCtn}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('snsr.label.문제 및 현상', '문제 및 현상')}</label>
                <CustomInputWithSearch
                  name="prbPnmnCtn"
                  placeholder={String(
                    t('snsr.msg.문제 및 현상을 입력해 주세요.', '문제 및 현상을 입력해 주세요.')
                  )}
                  value={newSensorSolutionReqStatusData?.prbPnmnCtn}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.등록자', '등록자')}</label>
                <CustomInputWithSearch
                  className="find"
                  name="dataInsUserNm"
                  placeholder={String(
                    t('com.label.등록자를 선택해 주세요.', '등록자를 선택해 주세요.')
                  )}
                  value={newSensorSolutionReqStatusData?.dataInsUserNm}
                  onChange={handleChange}
                  onSearchClick={() => setOpenElmEmpPopup(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.등록일', '등록일')}</label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    value={{
                      startDate: newSensorSolutionReqStatusData?.startDate || null,
                      endDate: newSensorSolutionReqStatusData?.endDate || null,
                    }}
                    onChange={(value) => {
                      setNewSensorSolutionReqStatusData({
                        ...newSensorSolutionReqStatusData,
                        startDate: String(value?.startDate || ''),
                        endDate: String(value?.endDate || ''),
                      });
                    }}
                    i18n={'ko'}
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    displayFormat={'YYYY.MM.DD'}
                  ></Datepicker>
                </DatePickerWrap>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            setReset={setNewSensorSolutionReqStatusData}
            onReset={() => {
              setDisplayCondition(
                (prev) =>
                  Object.keys(prev).reduce(
                    (acc, cur) => Object.assign(acc, { [cur]: '' }),
                    {}
                  ) as DisplayCondition
              );
            }}
            onSearch={searchBtn}
          />
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.솔루션 요청 list', '솔루션 요청 list')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="request"
            onClick={() => handleOpenNewSensorRequest()}
          >
            {t('com.button.신규센서솔루션 요청', '신규센서솔루션 요청')}
          </Button>
          <Button
            css={IconButton.button}
            className="copyRow"
            onClick={(e) => handleOpenCopyNewSensorRequest(e)}
          >
            {t('com.button.복사', '복사')}
          </Button>
          <Button css={IconButton.button} className="Exceldown" onClick={handleDownloadExcel}>
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={searchBtn}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'newSensorSolutionReqStatusPage'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.invalidate();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>

      <CustomGrid
        id={'newSensorSolutionReqStatusPage'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={480}
        autoCheck={true}
        isReadOnly={true}
        initialized={onInitialized}
      />

      {isOpenElmEmpPopup && (
        <ElmEmpPopup
          userData={userData}
          setUserData={setUserData}
          setPopup={setOpenElmEmpPopup}
          close={() => setOpenElmEmpPopup(false)}
          initParam={newSensorSolutionReqStatusData.dataInsUserNm}
        />
      )}
      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          singleSelect={false}
          downloadOnly={fileUploadModalCondition.downloadOnly}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            if (fileUploadModalCondition.target) {
              fileUploadModalCondition.target.node?.setDataValue(
                fileUploadModalCondition.target.column?.getColId(),
                fileCount
              );
            }
            setOpenFileUploadModal(false);
          }}
        />
      )}
    </>
  );
};

export default NewSensorSolutionReqStatusPage;
